/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMessagePlacementHooks } from '@atlassian/post-office-context/message-placement';
import type { MessageCategory } from '@post-office/placement-common/types';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { type ComponentType, useEffect, useState } from 'react';

import { MessageRenderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { withJsmSectionHeaderIntlProvider } from '../__generated__/intl-provider';
import { type Placement as JsmSectionHeaderPlacement } from '../contracts/Placement';
import { type Fetcher } from '@atlassian/post-office-placement-data/types';
import { type OnPerformanceTrackingEvent } from '@post-office/placement-contexts/create-placement-component/hooks/usePerformanceTracking/types';
import {
	type ContextWithRoute,
	type FallbackRenderFacade,
} from '@post-office/placement-contexts/create-placement-component/types';
import { type ErrorBoundaryPropsWithRender } from 'react-error-boundary';

type State = {
	messageInstanceId: string;
	messageTemplateId: any;
	messageCategory: MessageCategory;
	currentUsage: number;
	usageLimit: number;
	recipientRole: string;
	subscription: string;
	cloudUrl: string;
}[];

export const JsmSectionHeaderPlacementComponent: ComponentType<
	Partial<ErrorBoundaryPropsWithRender> & {
		context: ContextWithRoute;
		initialData: Promise<Response | undefined> | undefined;
		fetch?: Fetcher;
		onEvent?: OnPerformanceTrackingEvent;
	} & JsmSectionHeaderPlacement &
		FallbackRenderFacade & {
			messages?: {
				[x: string]: string;
			};
		}
> = withJsmSectionHeaderIntlProvider(
	createPlacementComponent<JsmSectionHeaderPlacement>(
		PLACEMENT_ID,
		({ initialData, openInProductHelp, link, screen }) => {
			const [state, setState] = useState<State>([]);

			const { data } = useInitialData<{ messages: State }>(initialData);

			useEffect(() => {
				setState((prevState) => data?.messages ?? prevState);
			}, [data]);

			const { onMessageDismiss } = useMessagePlacementHooks();
			onMessageDismiss((dismissedMessage) => {
				setState((prevState) =>
					prevState.filter(
						(message) => message.messageInstanceId !== dismissedMessage.messageInstanceId,
					),
				);
			});

			return (
				<>
					{state.slice(0, 1).map(({ messageInstanceId, ...props }) => (
						<MessageRenderer
							{...props}
							key={messageInstanceId}
							id={messageInstanceId}
							messageInstanceId={messageInstanceId}
							openInProductHelp={openInProductHelp}
							link={link}
							screen={screen}
						/>
					))}
				</>
			);
		},
	),
);

export default JsmSectionHeaderPlacementComponent;
